import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../api";
import { usePageTitle } from "../components/AppLayout";
import { Btn, Field, G_DARK, Input, PasswordInput, PasswordRequirements, globalStyles } from "../components/Shared";

// The two signed-out pages of a password (N5, pattern from data-portal ForgotPage /
// ResetPasswordPage). Both are portal-host routes (App.js) and need no sign-in.
//
//   /forgot-password   POST /api/auth/forgot-password {email}: the server answers the same
//                      sentence for every address (nothing to enumerate), shown as it came; 403
//                      while password sign-in is off (its sentence, as an alert).
//   /set-password#token=<raw>
//                      the link of an invitation (an admin created the account) or of a reset
//                      (forgot password); the token is in the FRAGMENT, so it never reaches a
//                      server log or a Referer. GET /api/auth/password-link?token= says whether
//                      it is still open ({email}; 400: used, expired or unknown), then
//                      POST /api/auth/reset-password {token, new_password}: 422 (a weak
//                      password; the link stays open) is shown and the form stays; 400 turns the
//                      page into "invalid or expired" with a way to ask for a new link.

export const ERROR_BOX = { padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)",
                           border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 };
const OK_BOX = { padding: "10px 14px", borderRadius: 8, background: "rgba(0,150,64,0.08)",
                 border: "1px solid rgba(0,150,64,0.25)", color: G_DARK, fontSize: 13 };
const LINK = { fontSize: 13, color: G_DARK, fontWeight: 500, textAlign: "center" };

/** The sign-in page's frame: the logo, a heading and one card. */
export function SignedOutShell({ heading, children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>
      <main style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/csm_binf.png" alt="BOKU Core Facility Bioinformatics" style={{ height: 60, borderRadius: 12, marginBottom: 16 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>{heading}</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "#4b5563" }}>Unio · BOKU Core Facility Bioinformatics</p>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 14 }}>
          {children}
        </div>
      </main>
    </div>
  );
}

export function ForgotPasswordPage() {
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim() });
      setMessage(r.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SignedOutShell heading="Forgot password">
      {message ? (
        <>
          <div role="status" style={OK_BOX}>{message}</div>
          <Link to="/login" style={LINK}>Back to sign-in</Link>
        </>
      ) : (
        <form onSubmit={submit} aria-label="Forgot password" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
            Type the e-mail address you sign in with. If it belongs to an account with a portal
            password, a link to set a new one is mailed to it. Accounts that sign in through
            Keycloak change their password there.
          </p>
          <Field label="E-mail" required>
            <Input type="email" autoComplete="email" spellCheck={false} value={email}
                   onChange={e => setEmail(e.target.value)} />
          </Field>
          {error && <div role="alert" style={ERROR_BOX}>{error}</div>}
          <Btn type="submit" disabled={busy || !email.trim()} style={{ justifyContent: "center", width: "100%" }}>
            {busy ? "Sending…" : "Send the link"}
          </Btn>
          <Link to="/login" style={LINK}>Back to sign-in</Link>
        </form>
      )}
    </SignedOutShell>
  );
}

export function tokenFromHash(hash) {
  const raw = (hash || "").replace(/^#/, "");
  return new URLSearchParams(raw).get("token") || "";
}

export function SetPasswordPage() {
  usePageTitle("Set your password");
  const location = useLocation();
  const [token] = useState(() => tokenFromHash(location.hash));
  // checking | open | invalid | done
  const [state, setState] = useState(token ? { step: "checking" } : { step: "invalid" });
  const [form, setForm] = useState({ password: "", again: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    api.get(`/auth/password-link?token=${encodeURIComponent(token)}`)
      .then(r => { if (live) setState({ step: "open", email: r.email }); })
      .catch(err => { if (live) setState({ step: "invalid", detail: err.status === 400 ? err.message : null,
                                           error: err.status === 400 ? null : err.message }); });
    return () => { live = false; };
  }, [token]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setError(null);
    if (form.password !== form.again) { setError("The two passwords do not match."); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: form.password });
      setState({ step: "done", message: r.message });
    } catch (err) {
      if (err.status === 400) setState({ step: "invalid", detail: err.message });
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (state.step === "checking") {
    body = <p role="status" style={{ margin: 0, fontSize: 14, color: "#4b5563" }}>Checking the link…</p>;
  } else if (state.step === "invalid") {
    body = (
      <>
        <div role="alert" style={ERROR_BOX}>
          {state.error
            ? `Could not check the link (${state.error}). Try again in a moment.`
            : <>This link is invalid or expired. A link works once, for 30 minutes.
                {state.detail && <> ({state.detail})</>}</>}
        </div>
        <Link to="/forgot-password" style={LINK}>Ask for a new link</Link>
        <Link to="/login" style={LINK}>Back to sign-in</Link>
      </>
    );
  } else if (state.step === "done") {
    body = (
      <>
        <div role="status" style={OK_BOX}>{state.message || "Your password is set."}</div>
        <Link to="/login" style={LINK}>Sign in</Link>
      </>
    );
  } else {
    body = (
      <form onSubmit={submit} aria-label="Set your password" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          Choose the password for <strong>{state.email}</strong>. You then sign in with this e-mail
          address and the new password.
        </p>
        {/* For password managers: which account the new password belongs to. */}
        <input type="text" autoComplete="username" value={state.email || ""} readOnly hidden />
        <Field label="New password" required>
          <PasswordInput autoComplete="new-password" value={form.password} onChange={set("password")} />
        </Field>
        <PasswordRequirements password={form.password} username={(state.email || "").split("@")[0]} />
        <Field label="Repeat the new password" required>
          <PasswordInput autoComplete="new-password" value={form.again} onChange={set("again")} />
        </Field>
        {error && <div role="alert" style={ERROR_BOX}>{error}</div>}
        <Btn type="submit" disabled={busy || !form.password || !form.again} style={{ justifyContent: "center", width: "100%" }}>
          {busy ? "Saving…" : "Set password"}
        </Btn>
      </form>
    );
  }
  return <SignedOutShell heading="Set your password">{body}</SignedOutShell>;
}
